// components/AssetsTab.tsx - Complete Asset Management Component
import React, { useState } from 'react';
import { 
  Truck, Trash2, Wrench, Search, Plus, MapPin, Fuel, Navigation, 
  Activity, AlertCircle, CheckCircle, Clock, Battery, MapPinned,
  Settings, Eye, Edit, X, Calendar, Phone, User
} from 'lucide-react';
import { Vehicle, Bin, Equipment, AssetTabType } from '../types';
import { getVehicleStatusColor, getBinStatusColor, getFuelLevelColor, getConditionColor } from '../utils';

interface AssetsTabProps {
  vehicles: Vehicle[];
  bins: Bin[];
  equipment: Equipment[];
  onUpdateVehicle?: (vehicle: Vehicle) => void;
  onUpdateBin?: (bin: Bin) => void;
  onUpdateEquipment?: (equipment: Equipment) => void;
}

const AssetsTab: React.FC<AssetsTabProps> = ({ 
  vehicles, 
  bins, 
  equipment,
  onUpdateVehicle,
  onUpdateBin,
  onUpdateEquipment
}) => {
  const [activeAssetTab, setActiveAssetTab] = useState<AssetTabType>('vehicles');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAssetModal, setShowAssetModal] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<Vehicle | Bin | Equipment | null>(null);
  const [assetType, setAssetType] = useState<'vehicle' | 'bin' | 'equipment'>('vehicle');

  const handleAssetClick = (asset: Vehicle | Bin | Equipment, type: 'vehicle' | 'bin' | 'equipment') => {
    setSelectedAsset(asset);
    setAssetType(type);
    setShowAssetModal(true);
  };

  const filterAssets = <T extends Vehicle | Bin | Equipment>(assets: T[], searchField: keyof T) => {
    return assets.filter(asset => {
      const matchesSearch = String(asset[searchField]).toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (asset as any).id?.toString().includes(searchTerm);
      
      if (statusFilter === 'all') return matchesSearch;
      return matchesSearch && (asset as any).status === statusFilter;
    });
  };

  const getAssetStats = () => {
    const vehicleStats = {
      total: vehicles.length,
      active: vehicles.filter(v => v.status === 'active').length,
      maintenance: vehicles.filter(v => v.status === 'maintenance').length,
      inactive: vehicles.filter(v => v.status === 'inactive').length,
      avgFuel: Math.round(vehicles.reduce((sum, v) => sum + v.fuelLevel, 0) / vehicles.length) || 0
    };

    const binStats = {
      total: bins.length,
      empty: bins.filter(b => b.status === 'empty').length,
      partial: bins.filter(b => b.status === 'partial').length,
      full: bins.filter(b => b.status === 'full').length,
      avgFill: Math.round(bins.reduce((sum, b) => sum + b.fillLevel, 0) / bins.length) || 0
    };

    const equipmentStats = {
      total: equipment.length,
      available: equipment.filter(e => e.status === 'available').length,
      inUse: equipment.filter(e => e.status === 'in-use').length,
      maintenance: equipment.filter(e => e.status === 'maintenance').length
    };

    return { vehicleStats, binStats, equipmentStats };
  };

  const { vehicleStats, binStats, equipmentStats } = getAssetStats();

  return (
    <div className="space-y-6">
      {/* Asset Type Navigation */}
      <div className="bg-white rounded-2xl p-2 flex space-x-2 shadow-sm">
        {[
          { id: 'vehicles' as AssetTabType, name: 'Vehicles', icon: Truck, count: vehicles.length },
          { id: 'bins' as AssetTabType, name: 'Bins', icon: Trash2, count: bins.length },
          { id: 'equipment' as AssetTabType, name: 'Equipment', icon: Wrench, count: equipment.length }
        ].map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveAssetTab(tab.id)}
              className={`flex-1 flex items-center justify-center space-x-2 py-3 rounded-xl transition-all duration-200 ${
                activeAssetTab === tab.id
                  ? 'bg-green-600 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Icon className="w-5 h-5" />
              <span className="font-medium">{tab.name}</span>
              <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">
                {tab.count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Search and Filters */}
      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder={`Search ${activeAssetTab}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-12 pr-4 py-4 w-full border border-gray-200 rounded-2xl focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white shadow-sm"
          />
        </div>

        {/* Status Filters */}
        <div className="flex space-x-2 overflow-x-auto pb-2">
          <button
            onClick={() => setStatusFilter('all')}
            className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
              statusFilter === 'all'
                ? 'bg-green-600 text-white shadow-lg'
                : 'bg-white text-gray-600 border border-gray-200'
            }`}
          >
            All
          </button>
          {activeAssetTab === 'vehicles' && ['active', 'maintenance', 'inactive'].map(status => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all capitalize ${
                statusFilter === status
                  ? 'bg-green-600 text-white shadow-lg'
                  : 'bg-white text-gray-600 border border-gray-200'
              }`}
            >
              {status}
            </button>
          ))}
          {activeAssetTab === 'bins' && ['empty', 'partial', 'full'].map(status => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all capitalize ${
                statusFilter === status
                  ? 'bg-green-600 text-white shadow-lg'
                  : 'bg-white text-gray-600 border border-gray-200'
              }`}
            >
              {status}
            </button>
          ))}
          {activeAssetTab === 'equipment' && ['available', 'in-use', 'maintenance'].map(status => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all capitalize ${
                statusFilter === status
                  ? 'bg-green-600 text-white shadow-lg'
                  : 'bg-white text-gray-600 border border-gray-200'
              }`}
            >
              {status.replace('-', ' ')}
            </button>
          ))}
        </div>
      </div>

      {/* Vehicles Section */}
      {activeAssetTab === 'vehicles' && (
        <div className="space-y-6">
          {/* Vehicle Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-4">
              <div className="flex items-center justify-between mb-2">
                <Truck className="w-8 h-8 text-green-600" />
                <span className="text-2xl font-bold text-green-600">{vehicleStats.active}</span>
              </div>
              <div className="text-sm text-green-700 font-medium">Active Vehicles</div>
            </div>
            <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-2xl p-4">
              <div className="flex items-center justify-between mb-2">
                <Wrench className="w-8 h-8 text-yellow-600" />
                <span className="text-2xl font-bold text-yellow-600">{vehicleStats.maintenance}</span>
              </div>
              <div className="text-sm text-yellow-700 font-medium">In Maintenance</div>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-4">
              <div className="flex items-center justify-between mb-2">
                <Fuel className="w-8 h-8 text-blue-600" />
                <span className="text-2xl font-bold text-blue-600">{vehicleStats.avgFuel}%</span>
              </div>
              <div className="text-sm text-blue-700 font-medium">Avg Fuel Level</div>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-4">
              <div className="flex items-center justify-between mb-2">
                <Activity className="w-8 h-8 text-purple-600" />
                <span className="text-2xl font-bold text-purple-600">{vehicleStats.total}</span>
              </div>
              <div className="text-sm text-purple-700 font-medium">Total Fleet</div>
            </div>
          </div>

          {/* Add Vehicle Button */}
          <button className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white py-4 rounded-2xl font-semibold flex items-center justify-center space-x-2 hover:from-green-600 hover:to-green-700 transition-all shadow-lg">
            <Plus className="w-5 h-5" />
            <span>Add New Vehicle</span>
          </button>

          {/* Vehicle List */}
          <div className="space-y-4">
            {filterAssets(vehicles, 'vehicleNo').map(vehicle => (
              <div 
                key={vehicle.id} 
                className="bg-white rounded-3xl border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow duration-200 cursor-pointer"
                onClick={() => handleAssetClick(vehicle, 'vehicle')}
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <Truck className="w-6 h-6 text-gray-600" />
                      <h3 className="text-lg font-bold text-gray-900">{vehicle.vehicleNo}</h3>
                    </div>
                    <p className="text-sm text-gray-600">{vehicle.type} • {vehicle.capacity}</p>
                    <div className="flex items-center space-x-2 text-xs text-gray-500 mt-1">
                      <User className="w-3 h-3" />
                      <span>Driver: {vehicle.driver}</span>
                      <Phone className="w-3 h-3 ml-2" />
                      <span>{vehicle.driverPhone}</span>
                    </div>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-xs font-semibold border ${getVehicleStatusColor(vehicle.status)}`}>
                    {vehicle.status}
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="bg-gray-50 rounded-xl p-3">
                    <div className="flex items-center space-x-2 mb-1">
                      <Fuel className="w-4 h-4 text-gray-400" />
                      <span className="text-xs text-gray-500">Fuel</span>
                    </div>
                    <div className={`font-bold ${getFuelLevelColor(vehicle.fuelLevel)}`}>
                      {vehicle.fuelLevel}%
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                      <div 
                        className={`h-2 rounded-full ${
                          vehicle.fuelLevel >= 70 ? 'bg-green-500' :
                          vehicle.fuelLevel >= 30 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${vehicle.fuelLevel}%` }}
                      ></div>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3">
                    <div className="flex items-center space-x-2 mb-1">
                      <Navigation className="w-4 h-4 text-gray-400" />
                      <span className="text-xs text-gray-500">KM Reading</span>
                    </div>
                    <div className="font-bold text-gray-700">{vehicle.kmReading.toLocaleString()}</div>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3">
                    <div className="flex items-center space-x-2 mb-1">
                      <Activity className="w-4 h-4 text-gray-400" />
                      <span className="text-xs text-gray-500">Condition</span>
                    </div>
                    <div className={`font-bold capitalize ${getConditionColor(vehicle.condition)}`}>
                      {vehicle.condition}
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-xl">
                    <div className="flex items-center space-x-2">
                      <MapPinned className="w-4 h-4 text-blue-500" />
                      <span className="text-sm text-gray-600">Current Location</span>
                    </div>
                    <span className="text-sm font-medium text-gray-900">{vehicle.currentLocation}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-xl">
                    <div className="flex items-center space-x-2">
                      <MapPin className="w-4 h-4 text-green-500" />
                      <span className="text-sm text-gray-600">Assigned Route</span>
                    </div>
                    <span className="text-sm font-medium text-gray-900">{vehicle.routeAssigned}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-xl">
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-4 h-4 text-yellow-500" />
                      <span className="text-sm text-gray-600">Next Service</span>
                    </div>
                    <span className="text-sm font-medium text-gray-900">{vehicle.nextMaintenance}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bins Section */}
      {activeAssetTab === 'bins' && (
        <div className="space-y-6">
          {/* Bin Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-2xl p-4">
              <div className="flex items-center justify-between mb-2">
                <AlertCircle className="w-8 h-8 text-red-600" />
                <span className="text-2xl font-bold text-red-600">{binStats.full}</span>
              </div>
              <div className="text-sm text-red-700 font-medium">Full Bins</div>
            </div>
            <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-2xl p-4">
              <div className="flex items-center justify-between mb-2">
                <Clock className="w-8 h-8 text-yellow-600" />
                <span className="text-2xl font-bold text-yellow-600">{binStats.partial}</span>
              </div>
              <div className="text-sm text-yellow-700 font-medium">Partially Filled</div>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-4">
              <div className="flex items-center justify-between mb-2">
                <CheckCircle className="w-8 h-8 text-green-600" />
                <span className="text-2xl font-bold text-green-600">{binStats.empty}</span>
              </div>
              <div className="text-sm text-green-700 font-medium">Empty Bins</div>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-4">
              <div className="flex items-center justify-between mb-2">
                <Battery className="w-8 h-8 text-blue-600" />
                <span className="text-2xl font-bold text-blue-600">{binStats.avgFill}%</span>
              </div>
              <div className="text-sm text-blue-700 font-medium">Avg Fill Level</div>
            </div>
          </div>

          {/* Add Bin Button */}
          <button className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-4 rounded-2xl font-semibold flex items-center justify-center space-x-2 hover:from-blue-600 hover:to-blue-700 transition-all shadow-lg">
            <Plus className="w-5 h-5" />
            <span>Add New Bin</span>
          </button>

          {/* Bin List */}
          <div className="space-y-4">
            {filterAssets(bins, 'binId').map(bin => (
              <div 
                key={bin.id} 
                className="bg-white rounded-3xl border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow duration-200 cursor-pointer"
                onClick={() => handleAssetClick(bin, 'bin')}
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <Trash2 className="w-6 h-6 text-gray-600" />
                      <h3 className="text-lg font-bold text-gray-900">{bin.binId}</h3>
                    </div>
                    <p className="text-sm text-gray-600">{bin.type} • {bin.capacity}</p>
                    <div className="flex items-center space-x-2 text-xs text-gray-500 mt-1">
                      <MapPin className="w-3 h-3" />
                      <span>{bin.location}</span>
                    </div>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-xs font-semibold border ${getBinStatusColor(bin.status)}`}>
                    {bin.status}
                  </div>
                </div>

                <div className="mb-4">
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-gray-600">Fill Level:</span>
                    <span className="font-bold">{bin.fillLevel}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-4">
                    <div 
                      className={`h-4 rounded-full ${
                        bin.fillLevel >= 80 ? 'bg-red-500' :
                        bin.fillLevel >= 50 ? 'bg-yellow-500' : 'bg-green-500'
                      }`}
                      style={{ width: `${bin.fillLevel}%` }}
                    ></div>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <div className="flex items-center space-x-2">
                      <MapPin className="w-4 h-4 text-gray-500" />
                      <span className="text-sm text-gray-600">Assigned Area</span>
                    </div>
                    <span className="text-sm font-medium text-gray-900">{bin.assignedArea}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-xl">
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-4 h-4 text-blue-500" />
                      <span className="text-sm text-gray-600">Last Emptied</span>
                    </div>
                    <span className="text-sm font-medium text-gray-900">{bin.lastEmptied}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-xl">
                    <div className="flex items-center space-x-2">
                      <Activity className="w-4 h-4 text-green-500" />
                      <span className="text-sm text-gray-600">Condition</span>
                    </div>
                    <span className={`text-sm font-medium capitalize ${getConditionColor(bin.condition)}`}>
                      {bin.condition}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Equipment Section */}
      {activeAssetTab === 'equipment' && (
        <div className="space-y-6">
          {/* Equipment Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-4">
              <div className="flex items-center justify-between mb-2">
                <CheckCircle className="w-8 h-8 text-green-600" />
                <span className="text-2xl font-bold text-green-600">{equipmentStats.available}</span>
              </div>
              <div className="text-sm text-green-700 font-medium">Available</div>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-4">
              <div className="flex items-center justify-between mb-2">
                <Activity className="w-8 h-8 text-blue-600" />
                <span className="text-2xl font-bold text-blue-600">{equipmentStats.inUse}</span>
              </div>
              <div className="text-sm text-blue-700 font-medium">In Use</div>
            </div>
            <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-2xl p-4">
              <div className="flex items-center justify-between mb-2">
                <Wrench className="w-8 h-8 text-yellow-600" />
                <span className="text-2xl font-bold text-yellow-600">{equipmentStats.maintenance}</span>
              </div>
              <div className="text-sm text-yellow-700 font-medium">Maintenance</div>
            </div>
          </div>

          {/* Add Equipment Button */}
          <button className="w-full bg-gradient-to-r from-purple-500 to-purple-600 text-white py-4 rounded-2xl font-semibold flex items-center justify-center space-x-2 hover:from-purple-600 hover:to-purple-700 transition-all shadow-lg">
            <Plus className="w-5 h-5" />
            <span>Add New Equipment</span>
          </button>

          {/* Equipment List */}
          <div className="space-y-4">
            {filterAssets(equipment, 'name').map(item => (
              <div 
                key={item.id} 
                className="bg-white rounded-3xl border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow duration-200 cursor-pointer"
                onClick={() => handleAssetClick(item, 'equipment')}
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <Wrench className="w-6 h-6 text-gray-600" />
                      <h3 className="text-lg font-bold text-gray-900">{item.name}</h3>
                    </div>
                    <p className="text-sm text-gray-600">Code: {item.code}</p>
                    <div className="flex items-center space-x-2 text-xs text-gray-500 mt-1">
                      <User className="w-3 h-3" />
                      <span>Assigned to: {item.assignedTo}</span>
                    </div>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-xs font-semibold border ${
                    item.status === 'available' ? 'bg-green-50 text-green-700 border-green-200' :
                    item.status === 'in-use' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                    'bg-yellow-50 text-yellow-700 border-yellow-200'
                  }`}>
                    {item.status.replace('-', ' ')}
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-4 h-4 text-gray-500" />
                      <span className="text-sm text-gray-600">Last Service</span>
                    </div>
                    <span className="text-sm font-medium text-gray-900">{item.lastService}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-xl">
                    <div className="flex items-center space-x-2">
                      <Activity className="w-4 h-4 text-blue-500" />
                      <span className="text-sm text-gray-600">Condition</span>
                    </div>
                    <span className={`text-sm font-medium capitalize ${getConditionColor(item.condition)}`}>
                      {item.condition}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Asset Details Modal */}
      {showAssetModal && selectedAsset && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-md rounded-3xl p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                {assetType === 'vehicle' ? 'Vehicle Details' :
                 assetType === 'bin' ? 'Bin Details' : 'Equipment Details'}
              </h2>
              <button
                onClick={() => setShowAssetModal(false)}
                className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-2xl flex items-center justify-center transition-colors"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            {assetType === 'vehicle' && (
              <VehicleDetails vehicle={selectedAsset as Vehicle} />
            )}

            {assetType === 'bin' && (
              <BinDetails bin={selectedAsset as Bin} />
            )}

            {assetType === 'equipment' && (
              <EquipmentDetails equipment={selectedAsset as Equipment} />
            )}

            <button
              onClick={() => setShowAssetModal(false)}
              className="w-full bg-gray-600 text-white py-3 rounded-2xl font-semibold hover:bg-gray-700 transition-colors mt-4"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Vehicle Details Component
const VehicleDetails: React.FC<{ vehicle: Vehicle }> = ({ vehicle }) => (
  <div className="space-y-4">
    <div className="bg-gray-50 rounded-2xl p-4">
      <h3 className="font-bold text-lg text-gray-900 mb-3">{vehicle.vehicleNo}</h3>
      <div className="space-y-2">
        <div className="flex justify-between">
          <span className="text-gray-600">Type:</span>
          <span className="font-medium">{vehicle.type}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Capacity:</span>
          <span className="font-medium">{vehicle.capacity}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Status:</span>
          <span className={`px-2 py-1 rounded-full text-xs font-semibold border ${getVehicleStatusColor(vehicle.status)}`}>
            {vehicle.status}
          </span>
        </div>
      </div>
    </div>

    <div className="bg-blue-50 rounded-2xl p-4">
      <h4 className="font-semibold text-gray-900 mb-3">Driver Information</h4>
      <div className="space-y-2">
        <div className="flex justify-between">
          <span className="text-gray-600">Name:</span>
          <span className="font-medium">{vehicle.driver}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Phone:</span>
          <span className="font-medium">{vehicle.driverPhone}</span>
        </div>
      </div>
    </div>

    <div className="bg-green-50 rounded-2xl p-4">
      <h4 className="font-semibold text-gray-900 mb-3">Vehicle Status</h4>
      <div className="space-y-3">
        <div className="flex justify-between">
          <span className="text-gray-600">Fuel Level:</span>
          <span className={`font-medium ${getFuelLevelColor(vehicle.fuelLevel)}`}>
            {vehicle.fuelLevel}%
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div 
            className={`h-3 rounded-full ${
              vehicle.fuelLevel >= 70 ? 'bg-green-500' :
              vehicle.fuelLevel >= 30 ? 'bg-yellow-500' : 'bg-red-500'
            }`}
            style={{ width: `${vehicle.fuelLevel}%` }}
          ></div>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">KM Reading:</span>
          <span className="font-medium">{vehicle.kmReading.toLocaleString()}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Condition:</span>
          <span className={`font-medium capitalize ${getConditionColor(vehicle.condition)}`}>
            {vehicle.condition}
          </span>
        </div>
      </div>
    </div>

    <div className="bg-yellow-50 rounded-2xl p-4">
      <h4 className="font-semibold text-gray-900 mb-3">Location & Route</h4>
      <div className="space-y-2">
        <div className="flex justify-between">
          <span className="text-gray-600">Current Location:</span>
          <span className="font-medium">{vehicle.currentLocation}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Assigned Route:</span>
          <span className="font-medium">{vehicle.routeAssigned}</span>
        </div>
      </div>
    </div>

    <div className="bg-purple-50 rounded-2xl p-4">
      <h4 className="font-semibold text-gray-900 mb-3">Maintenance</h4>
      <div className="space-y-2">
        <div className="flex justify-between">
          <span className="text-gray-600">Last Service:</span>
          <span className="font-medium">{vehicle.lastMaintenance}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Next Service:</span>
          <span className="font-medium">{vehicle.nextMaintenance}</span>
        </div>
      </div>
    </div>

    <div className="grid grid-cols-2 gap-3">
      <button className="py-3 px-4 bg-blue-600 text-white rounded-2xl font-medium hover:bg-blue-700 transition-colors">
        Track Live
      </button>
      <button className="py-3 px-4 bg-green-600 text-white rounded-2xl font-medium hover:bg-green-700 transition-colors">
        Edit Details
      </button>
    </div>
  </div>
);

// Bin Details Component
const BinDetails: React.FC<{ bin: Bin }> = ({ bin }) => (
  <div className="space-y-4">
    <div className="bg-gray-50 rounded-2xl p-4">
      <h3 className="font-bold text-lg text-gray-900 mb-3">{bin.binId}</h3>
      <div className="space-y-2">
        <div className="flex justify-between">
          <span className="text-gray-600">Type:</span>
          <span className="font-medium">{bin.type}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Capacity:</span>
          <span className="font-medium">{bin.capacity}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Location:</span>
          <span className="font-medium">{bin.location}</span>
        </div>
      </div>
    </div>

    <div className="bg-blue-50 rounded-2xl p-4">
      <h4 className="font-semibold text-gray-900 mb-3">Fill Status</h4>
      <div className="mb-3">
        <div className="flex justify-between mb-2">
          <span className="text-gray-600">Fill Level:</span>
          <span className="font-bold">{bin.fillLevel}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-4">
          <div 
            className={`h-4 rounded-full ${
              bin.fillLevel >= 80 ? 'bg-red-500' :
              bin.fillLevel >= 50 ? 'bg-yellow-500' : 'bg-green-500'
            }`}
            style={{ width: `${bin.fillLevel}%` }}
          ></div>
        </div>
      </div>
      <div className="space-y-2">
        <div className="flex justify-between">
          <span className="text-gray-600">Status:</span>
          <span className={`px-2 py-1 rounded-full text-xs font-semibold border ${getBinStatusColor(bin.status)}`}>
            {bin.status}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Last Emptied:</span>
          <span className="font-medium">{bin.lastEmptied}</span>
        </div>
      </div>
    </div>

    <div className="bg-green-50 rounded-2xl p-4">
      <h4 className="font-semibold text-gray-900 mb-3">Area & Condition</h4>
      <div className="space-y-2">
        <div className="flex justify-between">
          <span className="text-gray-600">Assigned Area:</span>
          <span className="font-medium">{bin.assignedArea}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Condition:</span>
          <span className={`font-medium capitalize ${getConditionColor(bin.condition)}`}>
            {bin.condition}
          </span>
        </div>
      </div>
    </div>

    <div className="grid grid-cols-2 gap-3">
      <button className="py-3 px-4 bg-red-600 text-white rounded-2xl font-medium hover:bg-red-700 transition-colors">
        Schedule Empty
      </button>
      <button className="py-3 px-4 bg-green-600 text-white rounded-2xl font-medium hover:bg-green-700 transition-colors">
        Update Status
      </button>
    </div>
  </div>
);

// Equipment Details Component
const EquipmentDetails: React.FC<{ equipment: Equipment }> = ({ equipment }) => (
  <div className="space-y-4">
    <div className="bg-gray-50 rounded-2xl p-4">
      <h3 className="font-bold text-lg text-gray-900 mb-3">{equipment.name}</h3>
      <div className="space-y-2">
        <div className="flex justify-between">
          <span className="text-gray-600">Equipment Code:</span>
          <span className="font-medium">{equipment.code}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Status:</span>
          <span className={`px-2 py-1 rounded-full text-xs font-semibold border ${
            equipment.status === 'available' ? 'bg-green-50 text-green-700 border-green-200' :
            equipment.status === 'in-use' ? 'bg-blue-50 text-blue-700 border-blue-200' :
            'bg-yellow-50 text-yellow-700 border-yellow-200'
          }`}>
            {equipment.status.replace('-', ' ')}
          </span>
        </div>
      </div>
    </div>

    <div className="bg-blue-50 rounded-2xl p-4">
      <h4 className="font-semibold text-gray-900 mb-3">Assignment</h4>
      <div className="space-y-2">
        <div className="flex justify-between">
          <span className="text-gray-600">Assigned To:</span>
          <span className="font-medium">{equipment.assignedTo}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Last Service:</span>
          <span className="font-medium">{equipment.lastService}</span>
        </div>
      </div>
    </div>

    <div className="bg-green-50 rounded-2xl p-4">
      <h4 className="font-semibold text-gray-900 mb-3">Condition</h4>
      <div className="flex justify-between">
        <span className="text-gray-600">Overall Condition:</span>
        <span className={`font-medium capitalize ${getConditionColor(equipment.condition)}`}>
          {equipment.condition}
        </span>
      </div>
    </div>

    <div className="grid grid-cols-2 gap-3">
      <button className="py-3 px-4 bg-blue-600 text-white rounded-2xl font-medium hover:bg-blue-700 transition-colors">
        Reassign
      </button>
      <button className="py-3 px-4 bg-green-600 text-white rounded-2xl font-medium hover:bg-green-700 transition-colors">
        Service Log
      </button>
    </div>
  </div>
);

export default AssetsTab;